import { Params } from '@angular/router';
import { User, UserRole, ViewLevel } from '@wam/shared';
import {
  Report,
  ReportLevel,
  ReportParameter,
  ReportParameterType,
  ReportProduct,
} from '@wap/reporting';

/**
 * Parameters to declare on the stand-in `Report` built by the reports app when there is no catalog
 * entry to take them from (see `ReportsAppComponent.buildFallbackReport()`), so
 * `ReportsService.getParameters()` still resolves the link's filters (already in the selected
 * facets) into the ids the datamart report components read: students/classes by level, plus the
 * date range for every level.
 */
export function buildFallbackReportParameters(level: ReportLevel): ReportParameter[] {
  const scopeIds: Partial<Record<ReportLevel, string[]>> = {
    [ReportLevel.STUDENT]: ['studentUUID', 'classUUID'],
    [ReportLevel.CLASS]: ['classUUID'],
  };

  return [
    ...(scopeIds[level] ?? []).map((id) => ({
      id,
      name: id,
      visible: false,
      required: false,
      type: ReportParameterType.SINGLE_SELECT,
    })),
    {
      id: 'startDate',
      name: 'startDate',
      visible: false,
      required: false,
      type: ReportParameterType.DATE,
    },
    {
      id: 'endDate',
      name: 'endDate',
      visible: false,
      required: false,
      type: ReportParameterType.DATE,
    },
  ];
}

/**
 * Query param carrying the FusionAuth id token that authorizes the standalone reports app.
 */
export const ID_TOKEN_QUERY_PARAM = 'idToken';

/**
 * Query param / local storage flag used to force the standalone reports app while developing.
 * It is only honoured on development hosts - see `isReportsApp()`.
 */
export const REPORTS_APP_FLAG = 'reportsApp';

/**
 * Query param carrying the organization for tokens without an `organization` claim - see
 * `resolveReportsOrganization()`.
 */
export const ORGANIZATION_QUERY_PARAM = 'organization';

/**
 * Query param carrying the JSON-encoded drill-down payload for a report opened from another
 * report (e.g. clicking a school row on a district usage report). Read back by
 * `ReportsAppComponent` and assigned directly to `OpenedReport.drillDownParams`.
 */
export const DRILL_DOWN_QUERY_PARAM = 'drillDownParams';

/**
 * Validates the JSON-encoded drill-down payload carried by `DRILL_DOWN_QUERY_PARAM` before it is
 * forwarded, unparsed, to report components (`AbstractDatamartReport`,
 * `PlaylistReportManagerComponent`, `LevelDetailReportComponent`, ...) that each call
 * `JSON.parse` on `OpenedReport.drillDownParams` without a try/catch. Those components only do so
 * when the field is truthy, so rejecting a malformed/tampered value here - by returning
 * `undefined` instead of the raw string - prevents them from throwing during report
 * initialization; the reports app then falls through to its own report-not-available error
 * instead (see `ReportsAppComponent.ngOnInit()`).
 *
 * Only plain JSON objects are accepted - the components above all expect to read named
 * properties (`product`, `schoolUUID`, `classUUID`, ...) off the parsed value.
 */
export function parseDrillDownParams(raw: string | undefined): string | undefined {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? raw
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Optional query param pre-selecting a strand on the reports flagged with
 * `ReportRouteDefinition.supportsStrandFilter` (student area of difficulty / objective details).
 * Ignored for every other report. Read back by `ReportsAppComponent` into `OpenedReport.strand`.
 */
export const STRAND_QUERY_PARAM = 'strand';

/**
 * Separator between the strand and the rest of a `skillArea` value on the student objective
 * details report, e.g. `Phonics - Letters and Letter Sounds`.
 */
export const SKILL_AREA_STRAND_SEPARATOR = ' - ';

/**
 * Normalizes the raw `strand` query param: trimmed, `undefined` when missing/blank.
 */
export function parseStrandParam(raw: unknown): string | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }

  const trimmed = raw.trim();
  return trimmed || undefined;
}

/**
 * Case-insensitive, whitespace tolerant strand name comparison - the `strand` link param is
 * typed/built outside of this app, so it must not have to match the datamart casing exactly.
 */
export function isSameStrand(a: string | null | undefined, b: string | null | undefined): boolean {
  const normalize = (value: string | null | undefined): string =>
    (value ?? '').trim().toLocaleLowerCase();
  const left = normalize(a);
  return !!left && left === normalize(b);
}

/**
 * Whether a student objective details `skillArea` (`<strand> - <area>`) belongs to `strand`,
 * judged by the part before the first `SKILL_AREA_STRAND_SEPARATOR` only - so a strand name merely
 * appearing later in the skill area does not match.
 */
export function skillAreaBelongsToStrand(
  skillArea: string | null | undefined,
  strand: string,
): boolean {
  const [skillAreaStrand] = (skillArea ?? '').split(SKILL_AREA_STRAND_SEPARATOR);
  return isSameStrand(skillAreaStrand, strand);
}

/**
 * Exhaustive list of hosts that serve the standalone reports app.
 *
 * Keep in sync with `dns_alias` in `deployment/terraform/<tier>/variables.tf`.
 */
const REPORTS_HOSTNAMES: ReadonlySet<string> = new Set([
  'reports.waterford.org', // prod
  'qareports.waterford.org', // qa
  'reports.dr.waterford.org', // dr
  'reports.qa-dr.waterford.org', // qa-dr
  'reports.localhost', // local development, no flag required
]);

/**
 * Ephemeral pull request review apps, e.g. `pr-219reports.waterfordapps.org`. The tier prefix is
 * derived from the branch name by `.jenkins/create-review-app.sh`, so it cannot be enumerated -
 * but it is pinned to the throwaway `waterfordapps.org` domain.
 */
const PR_REPORTS_HOSTNAME = /^[a-z0-9-]*reports\.waterfordapps\.org$/;

/**
 * Domains that serve real Waterford products. The `reportsApp` override is never honoured here, so
 * appending `?reportsApp=true` to `teacher.waterford.org` cannot turn it into the reports app.
 */
const PRODUCT_DOMAIN = /(^|\.)waterford(apps)?\.org$/;

/**
 * Roles allowed to open a report through the standalone reports app.
 */
export const REPORTS_APP_ALLOWED_ROLES: readonly UserRole[] = [
  UserRole.INTERNAL_ADMIN,
  UserRole.DISTRICT_ADMIN,
  UserRole.SCHOOL_ADMIN,
  UserRole.TEACHER,
  UserRole.EXT_PARENT,
];

/**
 * Query params that are forwarded to the reports NgRx state as selected facets.
 * Everything else (idToken, curriculum, reportsApp, ...) is deliberately ignored.
 */
export const REPORT_FILTER_QUERY_PARAMS: readonly string[] = [
  'schools',
  'classes',
  'students',
  'gradeLevels',
  'classGradeLevels',
  'goalStatus',
  'resourceType',
  'genders',
  'primaryLanguages',
  'benchmark',
  'benchmarks',
  'tags',
  'dates',
  'historicalDates',
];

/**
 * sessionStorage key used to stash the query params (minus the id token) before the URL is
 * sanitized. Scoped to the tab, cleared automatically once it is closed.
 */
const SESSION_STORAGE_QUERY_PARAMS_KEY = 'reportsAppQueryParams';

let cachedIsReportsApp: boolean | null = null;

/**
 * Determines whether the application runs in "standalone reports" mode.
 *
 * Deployed environments are recognised by hostname only, against a closed allow-list. On any other
 * Waterford product host the answer is always `false` - there is no way to talk the teacher or
 * admin app into booting as the reports app.
 *
 * The `reportsApp` query param / local storage override exists purely for development hosts
 * (`localhost`, ngrok tunnels, dev containers, ...) where the hostname carries no meaning.
 *
 * The result is cached because it is queried from interceptors, guards and the router setup, and
 * the hostname cannot change during the lifetime of the page.
 */
export function isReportsApp(
  hostname: string = globalThis.location.hostname,
  search: string = globalThis.location.search,
): boolean {
  if (cachedIsReportsApp !== null) {
    return cachedIsReportsApp;
  }

  const host = hostname.toLowerCase();

  if (REPORTS_HOSTNAMES.has(host) || PR_REPORTS_HOSTNAME.test(host)) {
    cachedIsReportsApp = true;
    return cachedIsReportsApp;
  }

  if (PRODUCT_DOMAIN.test(host)) {
    cachedIsReportsApp = false;
    return cachedIsReportsApp;
  }

  const requested = new URLSearchParams(search).get(REPORTS_APP_FLAG);
  if (requested !== null) {
    localStorage.setItem(REPORTS_APP_FLAG, `${requested !== 'false'}`);
  }

  cachedIsReportsApp = localStorage.getItem(REPORTS_APP_FLAG) === 'true';
  return cachedIsReportsApp;
}

/**
 * Only exported for tests - resets the memoized host detection.
 */
export function resetReportsAppDetection(): void {
  cachedIsReportsApp = null;
}

export function hasReportsAppAccess(roles: UserRole[] | undefined): boolean {
  return (roles ?? []).some((role) => REPORTS_APP_ALLOWED_ROLES.includes(role));
}

/**
 * Stashes the incoming query params (everything except the id token and the dev-only
 * `reportsApp` flag) in `sessionStorage`, then strips the search string from the visible URL via
 * `history.replaceState`.
 *
 * Must run synchronously before the router performs its initial navigation - i.e. from the app
 * initializer, not from a component - so the sensitive `idToken` never lingers in the address bar,
 * browser history or gets forwarded through a `Referer` header. `ReportsAppComponent` reads the
 * stashed params back with `consumeReportsAppQueryParams()`.
 *
 * The headless "printing" routes (`reports/<suffix>-printing`, hit by the PDF generator - see
 * `AbstractReport.exportAsPDF()`) are exempt from the full stash-and-strip below: those report
 * components read `reportParameters` / `appliedFilters` directly off
 * `ActivatedRoute.snapshot.queryParams`, not from the sessionStorage stash, so wiping the whole
 * search string before the router's initial navigation would leave them with nothing to render.
 * The `idToken` those routes carry (see `AbstractReport.printAuthQueryParam()`) still must not
 * linger though - the pdf-generator's headless browser then makes same-origin API/resource
 * requests off this page, which (per the repository's `no-referrer-when-downgrade` policy) can
 * forward the full query string as the `Referer`, and it would otherwise remain in browser
 * history too - so `stripIdTokenFromPrintingUrl()` removes only that param, preserving everything
 * else `ActivatedRoute` still needs.
 */
export function sanitizeReportsAppUrl(search: string = globalThis.location.search): void {
  if (isReportsAppPrintingRoute()) {
    stripIdTokenFromPrintingUrl(search);
    return;
  }

  const params = new URLSearchParams(search);
  params.delete(ID_TOKEN_QUERY_PARAM);
  params.delete(REPORTS_APP_FLAG);

  const serialized = params.toString();
  if (serialized) {
    sessionStorage.setItem(SESSION_STORAGE_QUERY_PARAMS_KEY, serialized);
  } else if (search) {
    sessionStorage.removeItem(SESSION_STORAGE_QUERY_PARAMS_KEY);
  }

  if (typeof history === 'undefined') {
    return;
  }

  const { pathname, hash } = globalThis.location;
  history.replaceState(history.state, '', `${pathname}${hash}`);
}

/**
 * Removes only `idToken` from a printing route's address bar via `history.replaceState`, leaving
 * every other query param intact for `ActivatedRoute.snapshot.queryParams` to still resolve once
 * the router's initial navigation runs - see `sanitizeReportsAppUrl()`.
 */
function stripIdTokenFromPrintingUrl(search: string): void {
  const params = new URLSearchParams(search);
  if (!params.has(ID_TOKEN_QUERY_PARAM)) {
    return;
  }

  params.delete(ID_TOKEN_QUERY_PARAM);

  if (typeof history === 'undefined') {
    return;
  }

  const { pathname, hash } = globalThis.location;
  const stringifiedParams: string = params.toString();
  const serialized: string = stringifiedParams ? `?${stringifiedParams}` : '';
  history.replaceState(history.state, '', `${pathname}${serialized}${hash}`);
}

/**
 * Matches the same `-printing` marker `AhaService.isPrintingContext()` already relies on to skip
 * unrelated bootstrap work for headless report renders.
 */
export function isReportsAppPrintingRoute(
  pathname: string = globalThis.location.pathname,
): boolean {
  return pathname.includes('-printing');
}

/**
 * Reads back the query params stashed by `sanitizeReportsAppUrl()`. Returns `{}` once nothing was
 * stored, e.g. the report was opened without any filters.
 */
export function consumeReportsAppQueryParams(): Params {
  const raw = sessionStorage.getItem(SESSION_STORAGE_QUERY_PARAMS_KEY);
  if (!raw) {
    return {};
  }

  const params: Params = {};
  new URLSearchParams(raw).forEach((value, key) => {
    params[key] = value;
  });
  return params;
}

/**
 * Only exported for tests - clears the stashed query params between specs.
 */
export function resetReportsAppQueryParams(): void {
  sessionStorage.removeItem(SESSION_STORAGE_QUERY_PARAMS_KEY);
}

const EXTRA_CURRICULUMS = new Set(['assessments', 'wacs']);

export function resolveCurriculum(value: string | undefined): ReportProduct {
  if (!value) {
    return ReportProduct.ERP;
  }

  const normalized = value.toLowerCase();
  const products = Object.values(ReportProduct) as string[];

  if (products.includes(normalized) || EXTRA_CURRICULUMS.has(normalized)) {
    return normalized as ReportProduct;
  }

  return ReportProduct.ERP;
}

/**
 * Mirrors the product/eligibility filtering the normal reports UI applies before listing a report
 * card (see `ReportsComponent.reports$`), so a standalone report link cannot match a catalog entry
 * the resolved curriculum isn't actually entitled to - e.g. `?curriculum=ems` opening an ERP-only
 * report and sending the wrong curriculum to the report API - nor a historical-only report when no
 * historical date range was requested (or vice versa).
 */
export function isReportEligible(
  report: Report,
  curriculum: ReportProduct | string,
  isHistoricalSelected: boolean,
): boolean {
  const effectiveProduct: ReportProduct =
    curriculum === 'assessments' ? ReportProduct.AYLA : (curriculum as ReportProduct);

  return (
    report.availableProducts.includes(effectiveProduct) &&
    (!isHistoricalSelected || report.historical === true)
  );
}

/**
 * The "unit" facet keys - each pins the report to a specific level of the org hierarchy, as
 * opposed to `dates`/`tags`/`gradeLevels`/... which merely narrow the data within that level.
 */
const UNIT_FACET_QUERY_PARAMS: Set<string> = new Set(['schools', 'classes', 'students']);

/**
 * Converts the incoming query params into the shape expected by
 * `reportsActions.updateStoreFromRoute` (which runs `extractFacetsWithLevel`).
 *
 * All filters are provided as comma separated values, e.g.
 * `?classes=uuid-1,uuid-2&dates=Last%20Week`. The facet matching the current view level has to
 * be JSON encoded, because that is the format the reports reducer expects.
 *
 * Unit facets (`schools`/`classes`/`students`) that do not belong to the requested `level` are
 * dropped instead of forwarded as-is: a stray `schools=...` param left over from copy-pasting a
 * `school-usage` link (or an old bookmark) must not silently scope down a `district-usage` report
 * to a single school. `students` browsing is the one exception that keeps `classes` too, since it
 * is a legitimate parent scope (e.g. "students in this class").
 */
export function buildReportFilterParams(queryParams: Params, level: ViewLevel): Params {
  const filters: Params = {};
  const allowedUnitFacets = new Set<string>([level.toString()]);
  if (level === ViewLevel.students) {
    allowedUnitFacets.add('classes');
  }

  REPORT_FILTER_QUERY_PARAMS.forEach((key) => {
    if (UNIT_FACET_QUERY_PARAMS.has(key) && !allowedUnitFacets.has(key)) {
      return;
    }

    const raw = queryParams[key];
    if (raw === undefined || raw === null || `${raw}` === '') {
      return;
    }

    if (key === level.toString()) {
      const values = `${raw}`
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => ({ value, display: value }));
      filters[key] = JSON.stringify(values);
      return;
    }

    filters[key] = `${raw}`;
  });

  return filters;
}

/**
 * Builds the absolute URL used to open a drill-down report in a new browser window/tab in the
 * standalone reports app.
 *
 * The new window is same-origin, so `ReportsAppAccessService` re-authorizes it from the
 * `currentUser` id token already persisted to `localStorage` - no `idToken` needs to be forwarded.
 */
export function buildReportsAppDrillDownUrl(
  routeName: string,
  drillDownParams: string,
  curriculum: string | undefined,
  origin: string = globalThis.location.origin,
  organization: string | undefined = getReportsAppOrganizationParam(),
): string {
  const params = new URLSearchParams();
  params.set(DRILL_DOWN_QUERY_PARAM, drillDownParams);

  if (curriculum) {
    params.set('curriculum', curriculum);
  }

  // The drill-down window has its own sessionStorage, so the link-provided organization (see
  // `resolveReportsOrganization()`) has to be forwarded explicitly for it to survive.
  if (organization) {
    params.set(ORGANIZATION_QUERY_PARAM, organization);
  }

  return `${origin}/${routeName}?${params.toString()}`;
}

/**
 * `organization` identifiers are interpolated straight into `/orgs/<organization>/...` request
 * paths (`DatamartService.post()`, `PlaylistReportManagerComponent.loadData$()`) - restricting the
 * link-provided query param to this shape before it is ever returned prevents a crafted
 * `?organization=` value (e.g. containing `/`, `?` or `#`) from altering that request's path or
 * appending extra query params, and rejects arbitrary/malformed identifiers outright.
 */
const ORGANIZATION_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * The `organization` query param carried by standalone report links. Always `undefined` outside
 * the reports app, and also `undefined` when the value is missing or does not look like a valid
 * identifier (see `ORGANIZATION_ID_PATTERN`).
 *
 * Normally read back from the stash written by `sanitizeReportsAppUrl()`. The headless printing
 * routes (opened by the pdf-generator in a fresh browser session - see
 * `AbstractReport.exportAsPDF()`) are exempt from that sanitization though, so nothing was ever
 * stashed there: read it straight off the live url instead.
 */
export function getReportsAppOrganizationParam(
  search: string = globalThis.location.search,
): string | undefined {
  if (!isReportsApp()) {
    return undefined;
  }

  const value = isReportsAppPrintingRoute()
    ? new URLSearchParams(search).get(ORGANIZATION_QUERY_PARAM)
    : consumeReportsAppQueryParams()[ORGANIZATION_QUERY_PARAM];
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return ORGANIZATION_ID_PATTERN.test(trimmed) ? trimmed : undefined;
}

/**
 * Organization to scope report data (datamart) requests to.
 *
 * The token's own `organization` claim always wins. Only tokens without one (e.g. `ext-parent`/
 * Upstart - see `AuthenticationService.buildUser()`) fall back to the `organization` query param
 * of the standalone report link - which is why this is deliberately *not* written back onto the
 * `User`: other org-scoped calls (catalog, academic session, ...) must keep being skipped for them.
 */
export function resolveReportsOrganization(
  user: Pick<User, 'organization'> | null | undefined,
): string | undefined {
  return user?.organization || getReportsAppOrganizationParam();
}
